import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Heart, MapPin, Fuel, Gauge, Calendar, Phone, Zap, Settings, Hash, Shield, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { FaWhatsapp } from 'react-icons/fa';
import verifiedBadge from '../assets/verified.png';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

const VerifiedBadge = () => (
  <div className="absolute top-2 left-2 z-10">
    <img src={verifiedBadge} alt="Verified" className="h-6 w-auto object-contain drop-shadow-sm" style={{ maxWidth: '88px' }} />
  </div>
);

const Wishlist = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  const [priceRange, setPriceRange] = useState([5000, 8000000]);
  const MIN_PRICE = 5000;
  const MAX_PRICE = 8000000;
  const [filters, setFilters] = useState({
    vehicle_type: '', fuel_type: '', transmission: '',
    verified_only: false, has_insurance: null, has_puc: null,
    is_choice_number: false,
  });
  const [sortBy, setSortBy] = useState('recently_added');

  useEffect(() => { if (!user) { navigate('/login'); return; } fetchCities(); fetchWishlist(); }, [user, token]);
  useEffect(() => { applyFilters(); }, [filters, priceRange, sortBy, allVehicles]);

  const fetchCities = async () => { try { const r = await axios.get(`${API}/cities`); setCities(r.data.cities); } catch {} };

  const fetchWishlist = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/wishlist?limit=200`, { headers: { Authorization: `Bearer ${token}` } });
      setAllVehicles(res.data.vehicles || []);
      setTotal(res.data.total || 0);
    } catch { toast.error('Failed to load wishlist'); }
    finally { setLoading(false); }
  };

  const applyFilters = () => {
    let list = [...allVehicles];
    if (filters.vehicle_type) list = list.filter(v => v.vehicle_type === filters.vehicle_type);
    if (filters.fuel_type) list = list.filter(v => v.fuel_type === filters.fuel_type);
    if (filters.transmission) list = list.filter(v => v.transmission === filters.transmission);
    if (filters.verified_only) list = list.filter(v => v.verified_by_x7autoz === true);
    if (filters.has_insurance === true)  list = list.filter(v => v.has_insurance === true);
    if (filters.has_insurance === false) list = list.filter(v => v.has_insurance === false);
    if (filters.has_puc === true)  list = list.filter(v => v.has_puc === true);
    if (filters.has_puc === false) list = list.filter(v => v.has_puc === false);
    if (filters.is_choice_number) list = list.filter(v => v.is_choice_number === true);
    list = list.filter(v => { const p = parseInt((v.price || '').replace(/[^0-9]/g, '')) || 0; return p >= priceRange[0] && p <= priceRange[1]; });
    if (sortBy === 'price_low_high') list = [...list].sort((a, b) => (parseInt((a.price||'').replace(/[^0-9]/g,''))||0) - (parseInt((b.price||'').replace(/[^0-9]/g,''))||0));
    else if (sortBy === 'price_high_low') list = [...list].sort((a, b) => (parseInt((b.price||'').replace(/[^0-9]/g,''))||0) - (parseInt((a.price||'').replace(/[^0-9]/g,''))||0));
    else if (sortBy === 'year_new_old') list = [...list].sort((a, b) => (b.manufacturing_year||0) - (a.manufacturing_year||0));
    setVehicles(list);
  };

  const handleRemoveFromWishlist = async (vehicleId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/wishlist/${vehicleId}`, { headers: { Authorization: `Bearer ${token}` } });
      setAllVehicles(prev => prev.filter(v => v.id !== vehicleId));
      setTotal(prev => prev - 1);
      toast.success('Removed from wishlist');
    } catch { toast.error('Failed to remove from wishlist'); }
  };

  const formatPrice = (v) => { if (v >= 10000000) return `₹${(v/10000000).toFixed(1)} Cr`; if (v >= 100000) return `₹${(v/100000).toFixed(1)} L`; if (v >= 1000) return `₹${(v/1000).toFixed(0)} K`; return `₹${v}`; };
  const getFuelColor = (fuel) => { if (!fuel) return 'text-gray-500'; const t = fuel.toLowerCase(); if (t.includes('petrol')||t.includes('cng')) return 'text-sky-600'; if (t.includes('diesel')) return 'text-orange-600'; if (t.includes('electric')) return 'text-green-600'; return 'text-gray-500'; };

  const clearFilters = () => {
    setFilters({ vehicle_type: '', fuel_type: '', transmission: '', verified_only: false, has_insurance: null, has_puc: null, is_choice_number: false });
    setPriceRange([MIN_PRICE, MAX_PRICE]); setSortBy('recently_added');
  };

  const vehicleTypes = [{ value: 'car', label: 'Cars' }, { value: 'bike', label: 'Bikes' }, { value: 'ev', label: 'Electric' }, { value: 'commercial', label: 'Commercial' }];
  const fuelTypes = [{ value: 'Petrol', label: 'Petrol' }, { value: 'Diesel', label: 'Diesel' }, { value: 'CNG', label: 'CNG' }, { value: 'Hybrid', label: 'Hybrid' }];
  const transmissions = [{ value: 'Manual', label: 'Manual' }, { value: 'Automatic', label: 'Automatic' }, { value: 'AMT', label: 'AMT' }, { value: 'CVT', label: 'CVT' }];
  const sortOptions = [{ value: 'recently_added', label: 'Recently Added' }, { value: 'price_low_high', label: 'Price: Low to High' }, { value: 'price_high_low', label: 'Price: High to Low' }, { value: 'year_new_old', label: 'Year: Newest First' }];

  const SkeletonCard = () => (
    <Card className="rounded-xl overflow-hidden border border-gray-200 animate-pulse">
      <div className="h-52 bg-gray-200" />
      <CardContent className="p-3 space-y-3"><div className="h-4 bg-gray-200 rounded w-3/4"/><div className="h-3 bg-gray-200 rounded w-1/2"/><div className="flex gap-2 mt-3"><div className="flex-1 h-9 bg-gray-200 rounded"/><div className="flex-1 h-9 bg-gray-200 rounded"/></div></CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Header selectedCity={selectedCity} setSelectedCity={setSelectedCity} cities={cities} />
      <div className="px-4 md:px-8 lg:px-12 py-8">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2 flex items-center gap-3">
            <Heart className="h-8 w-8 text-red-500 fill-red-500" />
            My <span className="text-orange-500">Wishlist</span>
          </h1>
          <p className="text-gray-600">{total} saved vehicle{total !== 1 ? 's' : ''}</p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* SIDEBAR */}
          <motion.aside initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }} className="lg:w-72">
            <div className="bg-[#eef1f6] rounded-2xl p-6 sticky top-4 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-blue-600 font-medium hover:bg-transparent">CLEAR ALL</Button>
              </div>

              {/* Sort */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Sort By</h3>
                <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger className="bg-white border-none rounded-xl h-12 shadow-sm"><SelectValue /></SelectTrigger><SelectContent>{sortOptions.map(o=><SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
              </div>

              {/* Price Range */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Price Range</h3>
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <Slider value={priceRange} onValueChange={setPriceRange} min={MIN_PRICE} max={MAX_PRICE} step={10000} className="mb-4"/>
                  <div className="flex justify-between gap-3 text-xs">
                    <div className="flex flex-col w-full"><span className="text-gray-400 mb-1">MIN</span><span className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">{formatPrice(priceRange[0])}</span></div>
                    <div className="flex flex-col w-full"><span className="text-gray-400 mb-1">MAX</span><span className="bg-gray-100 px-3 py-2 rounded-lg text-sm font-medium text-gray-700">{formatPrice(priceRange[1])}</span></div>
                  </div>
                </div>
              </div>

              {/* Verification Status */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Verification Status</h3>
                <button onClick={() => setFilters(prev => ({ ...prev, verified_only: !prev.verified_only }))} className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${filters.verified_only ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
                  <Shield className={`h-5 w-5 flex-shrink-0 ${filters.verified_only ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800">Verified Vehicles Only</p>
                    <p className="text-xs text-gray-500">Show only X7 Autoz verified</p>
                  </div>
                  <div className={`ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${filters.verified_only ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                    {filters.verified_only && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                  </div>
                </button>
              </div>

              {/* Insurance */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Insurance</h3>
                <div className="flex gap-2">
                  {[{ label: 'Any', value: null }, { label: 'Insured', value: true }, { label: 'No Cover', value: false }].map(opt => (
                    <button key={String(opt.value)} onClick={() => setFilters(f => ({ ...f, has_insurance: opt.value }))}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all ${
                        filters.has_insurance === opt.value
                          ? opt.value === true ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : opt.value === false ? 'border-red-400 bg-red-50 text-red-700'
                          : 'border-gray-400 bg-gray-100 text-gray-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              {/* PUC */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">PUC Certificate</h3>
                <div className="flex gap-2">
                  {[{ label: 'Any', value: null }, { label: 'Valid PUC', value: true }, { label: 'No PUC', value: false }].map(opt => (
                    <button key={String(opt.value)} onClick={() => setFilters(f => ({ ...f, has_puc: opt.value }))}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium border-2 transition-all ${
                        filters.has_puc === opt.value
                          ? opt.value === true ? 'border-green-500 bg-green-50 text-green-700'
                          : opt.value === false ? 'border-red-400 bg-red-50 text-red-700'
                          : 'border-gray-400 bg-gray-100 text-gray-700'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              {/* Choice Number */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Registration Number</h3>
                <button
                  onClick={() => setFilters(prev => ({ ...prev, is_choice_number: !prev.is_choice_number }))}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${filters.is_choice_number ? 'border-orange-500 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                >
                  <Star className={`h-5 w-5 flex-shrink-0 ${filters.is_choice_number ? 'text-orange-500 fill-orange-500' : 'text-gray-400'}`} />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-800">Choice Numbers Only</p>
                    <p className="text-xs text-gray-500">Fancy / VIP registration plates</p>
                  </div>
                  <div className={`ml-auto w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${filters.is_choice_number ? 'border-orange-500 bg-orange-500' : 'border-gray-300'}`}>
                    {filters.is_choice_number && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                  </div>
                </button>
              </div>

              {/* Vehicle Type */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Vehicle Type</h3>
                <div className="flex flex-wrap gap-2">
                  {vehicleTypes.map(type=>(<button key={type.value} onClick={()=>setFilters(f=>({...f,vehicle_type:f.vehicle_type===type.value?'':type.value}))} className={`px-4 py-2 rounded-full text-sm font-medium transition ${filters.vehicle_type===type.value?'bg-blue-600 text-white':'bg-white text-gray-700'}`}>{type.label}</button>))}
                </div>
              </div>

              {/* Fuel Type */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Fuel Type</h3>
                <div className="flex flex-wrap gap-2">
                  {fuelTypes.map(f=>(<button key={f.value} onClick={()=>setFilters(prev=>({...prev,fuel_type:prev.fuel_type===f.value?'':f.value}))} className={`px-4 py-2 rounded-full text-sm font-medium transition ${filters.fuel_type===f.value?'bg-blue-600 text-white':'bg-white text-gray-700'}`}>{f.label}</button>))}
                </div>
              </div>

              {/* Transmission */}
              <div className="mb-6">
                <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Transmission</h3>
                <div className="flex flex-wrap gap-2">
                  {transmissions.map(t=>(<button key={t.value} onClick={()=>setFilters(prev=>({...prev,transmission:prev.transmission===t.value?'':t.value}))} className={`px-4 py-2 rounded-full text-sm font-medium transition ${filters.transmission===t.value?'bg-blue-600 text-white':'bg-white text-gray-700'}`}>{t.label}</button>))}
                </div>
              </div>
            </div>
          </motion.aside>

          {/* VEHICLE GRID */}
          <main className="flex-1">
            {loading && (<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{Array.from({length:6}).map((_,i)=><SkeletonCard key={i}/>)}</div>)}

            {!loading && vehicles.length === 0 && (
              <Card><CardContent className="p-12 text-center">
                <Heart className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                {filters.is_choice_number ? (
                  <><p className="text-gray-600 text-lg mb-2">No choice number vehicles in your wishlist</p><Button onClick={()=>setFilters(prev=>({...prev,is_choice_number:false}))}>Show All Wishlisted</Button></>
                ) : filters.verified_only ? (
                  <><p className="text-gray-600 text-lg mb-2">No verified vehicles in your wishlist</p><Button onClick={()=>setFilters(prev=>({...prev,verified_only:false}))}>Show All Wishlisted</Button></>
                ) : (
                  <><p className="text-gray-600 text-lg mb-4">Your wishlist is empty</p><Button onClick={()=>navigate('/browse')} className="bg-orange-500 hover:bg-orange-600 text-white">Browse Vehicles</Button></>
                )}
              </CardContent></Card>
            )}

            {!loading && vehicles.length > 0 && (
              <>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <p className="text-gray-600 text-sm">{vehicles.length} vehicle{vehicles.length!==1?'s':''} shown</p>
                  {filters.verified_only && <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-3 py-1 rounded-full font-semibold"><Shield className="h-3 w-3"/>Verified Only</span>}
                  {filters.is_choice_number && <span className="flex items-center gap-1 text-xs bg-orange-100 text-orange-700 px-3 py-1 rounded-full font-semibold"><Star className="h-3 w-3 fill-orange-500"/>Choice Numbers</span>}
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {vehicles.map((vehicle, idx) => (
                    <motion.div key={vehicle.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(idx * 0.04, 0.3) }}>
                      <Card onClick={() => navigate(`/vehicles/${vehicle.id}`)} className="cursor-pointer hover:shadow-lg transition-all duration-300 rounded-xl overflow-hidden border border-gray-200">
                        <div className="relative h-52 overflow-hidden bg-gray-100">
                          <img src={vehicle.images?.[0]||'https://images.unsplash.com/photo-1676288176820-a5a954d81e6e?w=500'} alt={`${vehicle.brand_name} ${vehicle.model_name}`} className="w-full h-full object-contain transition-transform duration-300"/>
                          {vehicle.verified_by_x7autoz && <VerifiedBadge />}
                          {vehicle.is_choice_number && (
                            <div className="absolute bottom-2 left-2 z-10">
                              <span className="inline-flex items-center gap-1 bg-orange-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow">
                                <Star className="h-2.5 w-2.5 fill-white" /> Choice No.
                              </span>
                            </div>
                          )}
                          <button onClick={(e)=>handleRemoveFromWishlist(vehicle.id,e)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 flex items-center justify-center shadow-md hover:bg-red-600 transition">
                            <Heart className="h-4 w-4 text-white fill-white"/>
                          </button>
                        </div>

                        <CardContent className="p-3">
                          <div className="flex justify-between items-start">
                            <h3 className="text-base font-semibold text-gray-800 leading-tight">{vehicle.brand_name} {vehicle.model_name}</h3>
                            <span className="text-amber-900 font-bold text-base">₹ {vehicle.price}</span>
                          </div>
                          <p className="text-xs text-gray-700 mt-1">{vehicle.variant_name}</p>

                          <div className="grid grid-cols-3 mt-3">
                            <div className="relative flex flex-col items-center px-3 pb-4 border-r border-gray-300"><Gauge className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.km_run_range||'0 KM'}</span><div className="absolute bottom-0 left-3 right-3 border-b border-gray-300"/></div>
                            <div className="relative flex flex-col items-center px-3 pb-4 border-r border-gray-300">{vehicle.fuel_type?.toLowerCase().includes('electric')?<Zap className={`h-4 w-4 mb-1 ${getFuelColor(vehicle.fuel_type)}`} fill="currentColor"/>:<Fuel className={`h-4 w-4 mb-1 ${getFuelColor(vehicle.fuel_type)}`}/>}<span className={`text-[10px] ${getFuelColor(vehicle.fuel_type)}`}>{vehicle.fuel_type}</span><div className="absolute bottom-0 left-3 right-3 border-b border-gray-300"/></div>
                            <div className="relative flex flex-col items-center px-3 pb-4"><Settings className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.transmission||'Manual'}</span><div className="absolute bottom-0 left-3 right-3 border-b border-gray-300"/></div>
                            <div className="flex flex-col items-center px-3 pt-3 border-r border-gray-300"><MapPin className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.city||'City'}</span></div>
                            <div className="flex flex-col items-center px-3 pt-3 border-r border-gray-300"><Calendar className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.manufacturing_year||'2022'}</span></div>
                            <div className="flex flex-col items-center px-3 pt-3"><Hash className="h-4 w-4 text-black mb-1"/><span className="text-[10px] text-black">{vehicle.registration_no||'KA-XX'}</span></div>
                          </div>

                          <div className="flex gap-2 mt-4 pt-3">
                            <Button className="flex-1 bg-white hover:bg-gray-100 border border-gray-300 text-black text-xs h-9" onClick={(e)=>{e.stopPropagation();if(!user)return navigate('/login');window.open(`tel:${vehicle.contact_phone}`);}}><Phone className="h-3.5 w-3.5 mr-1"/>Call</Button>
                            <Button className="flex-1 bg-green-500 hover:bg-green-600 text-white text-xs h-9" onClick={(e)=>{e.stopPropagation();if(!user)return navigate('/login');const msg=encodeURIComponent(`Hi, I'm interested in your ${vehicle.brand_name} ${vehicle.model_name}`);window.open(`https://wa.me/${vehicle.contact_phone}?text=${msg}`,'_blank');}}><FaWhatsapp className="h-3.5 w-3.5 mr-1"/>WhatsApp</Button>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>
              </>
            )}
          </main>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Wishlist;